/**
 * Team entries for public tournament surfaces.
 *
 * A team-division entry (`event_group.is_team = true`) is one registration row for
 * the player who signed up; the team is described only by the free-text
 * `team_name` (defaults to the registrant's own name) and `team_info` roster.
 * Draw slots, matches, schedules and check-ins reference that lead player's id,
 * so every surface would otherwise show one personal name and hide the team.
 *
 * These helpers resolve, for a lead player, the team the surface should show:
 * `name` (the team name the entrant typed, or null when they left it blank) and
 * `roster` (the team description exactly as typed, or null). Nothing here parses
 * `team_info`; it has no fixed shape and is only ever shown verbatim or with its
 * line breaks collapsed.
 */

export type TeamEntry = {
  /** Team name to use as the participant label; null → show the personal name. */
  name: string | null;
  /** Roster / team description verbatim (line breaks preserved); null when absent. */
  roster: string | null;
};

type TeamDivision = { is_team: boolean | null };

type RegistrantPlayer = { first_name: string | null; last_name: string | null } | null;

type TeamRegistrant = {
  player_id: string;
  team_name: string | null;
  team_info?: string | null;
  status?: string | null;
  player?: RegistrantPlayer;
};

type EventTeamRegistrant = TeamRegistrant & {
  event_group_id: string;
  event_group?: TeamDivision | null;
};

/**
 * Registration stores the entrant's own "First Last" as `team_name` when no team
 * name was typed (see `resolveTeamFields`), so a stored value equal to that
 * default is "no team name" and must keep going through the script-aware
 * personal-name formatting.
 */
function isDefaultTeamName(label: string, player: RegistrantPlayer | undefined): boolean {
  if (!player) return false;
  const defaultName = [player.first_name, player.last_name].filter(Boolean).join(' ').trim();
  return defaultName !== '' && label === defaultName;
}

/** The team a single registration describes, or null when it carries nothing. */
export function resolveTeamEntry(r: TeamRegistrant): TeamEntry | null {
  const typed = (r.team_name ?? '').trim();
  const name = typed && !isDefaultTeamName(typed, r.player) ? typed : null;
  const roster = (r.team_info ?? '').trim() || null;
  if (!name && !roster) return null;
  return { name, roster };
}

/**
 * Fold registrations into a map, one entry per key. When a player holds several
 * registrations in the same division (a withdrawn one replaced by a new entry),
 * the non-withdrawn one wins; otherwise the first seen wins.
 */
function foldEntries<R extends TeamRegistrant>(
  rows: readonly R[],
  keyOf: (r: R) => string | null,
): Map<string, TeamEntry> {
  const map = new Map<string, TeamEntry>();
  const withdrawn = new Set<string>();
  for (const r of rows) {
    const key = keyOf(r);
    if (!key) continue;
    const entry = resolveTeamEntry(r);
    if (!entry) continue;
    const isWithdrawn = r.status === 'withdrawn';
    if (isWithdrawn) {
      // Only fill a gap; never override an active registration's entry.
      if (!map.has(key)) {
        map.set(key, entry);
        withdrawn.add(key);
      }
      continue;
    }
    if (!map.has(key) || withdrawn.has(key)) {
      map.set(key, entry);
      withdrawn.delete(key);
    }
  }
  return map;
}

/** Map of player id → team entry for one division. Empty unless it is a team division. */
export function buildTeamEntryMap(
  group: TeamDivision,
  registrants: readonly TeamRegistrant[],
): Map<string, TeamEntry> {
  if (!group.is_team) return new Map();
  return foldEntries(registrants, (r) => r.player_id);
}

/**
 * Map of player id → team label for one division (name only). Empty unless the
 * division is a team division; entries without a team name are omitted so
 * callers fall back to the personal name.
 */
export function buildTeamLabelMap(
  group: TeamDivision,
  registrants: readonly TeamRegistrant[],
): Map<string, string> {
  const labels = new Map<string, string>();
  for (const [playerId, entry] of buildTeamEntryMap(group, registrants)) {
    if (entry.name) labels.set(playerId, entry.name);
  }
  return labels;
}

/** Key for event-wide lookups: the same player can lead teams in several divisions. */
export function teamEntryKey(groupId: string, playerId: string): string {
  return `${groupId}:${playerId}`;
}

/**
 * Event-wide map keyed by `teamEntryKey(event_group_id, player_id)`. Rows whose
 * `event_group` is present and not a team division are skipped; rows without an
 * `event_group` join are assumed to come from a team-only query.
 */
export function buildEventTeamEntryMap(
  rows: readonly EventTeamRegistrant[],
): Map<string, TeamEntry> {
  return foldEntries(rows, (r) => {
    if (r.event_group && !r.event_group.is_team) return null;
    return teamEntryKey(r.event_group_id, r.player_id);
  });
}

/** Plain-object form for JSON API responses. */
export function teamEntriesToRecord(map: Map<string, TeamEntry>): Record<string, TeamEntry> {
  return Object.fromEntries(map);
}

/** Participant label: the team name when there is one, else the personal name. */
export function teamLabel(entry: TeamEntry | null | undefined, personalName: string): string {
  return entry?.name?.trim() || personalName;
}

/** The roster's lines as typed: trimmed, blank lines dropped. Nothing else is parsed. */
export function rosterLines(roster: string | null | undefined): string[] {
  if (!roster) return [];
  return roster
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

/** Roster on one line for compact layouts: "Jared Yeo · Clive Lim". */
export function rosterInline(roster: string | null | undefined): string | null {
  const parts = rosterLines(roster);
  return parts.length ? parts.join(' · ') : null;
}

type StandingLike = {
  playerId: string;
  playerName: string;
  matchBreakdowns?: Array<{ opponentId?: string; opponentName: string }>;
};

/**
 * Standings rows carry the lead player's name; relabel each row (and its
 * head-to-head opponents, matched by id) with the team label. Rows whose
 * player has no team label are returned as-is.
 */
export function applyTeamLabelsToStandings<T extends StandingLike>(
  standings: readonly T[],
  labelFor: (playerId: string) => string | undefined,
): T[] {
  return standings.map((s) => {
    const own = labelFor(s.playerId);
    let opponentsChanged = false;
    const breakdowns = s.matchBreakdowns?.map((b) => {
      const opp = b.opponentId ? labelFor(b.opponentId) : undefined;
      if (!opp || opp === b.opponentName) return b;
      opponentsChanged = true;
      return { ...b, opponentName: opp };
    });
    if (!own && !opponentsChanged) return s;
    return {
      ...s,
      playerName: own ?? s.playerName,
      ...(breakdowns ? { matchBreakdowns: breakdowns } : {}),
    };
  });
}

/** Ellipsis truncation for team labels: `maxLength` is the total length including "…". */
export function truncateLabel(label: string, maxLength?: number): string {
  if (!maxLength || maxLength <= 0 || label.length <= maxLength) return label;
  return `${label.slice(0, maxLength - 1)}…`;
}

type FormatName = (
  firstName: string | null,
  lastName: string | null,
  alias?: string | null,
  options?: { maxLength?: number },
) => string;

type LabelledPlayer = {
  firstName: string;
  lastName: string;
  displayName?: string;
  /** A trimmed, non-empty alias beats the personal name (product ruling 2026-09-21). */
  alias?: string | null;
};

/**
 * Label for a draw participant. Mirrors `resolveParticipantLabel`'s ladder (team →
 * alias → personal name) but cannot delegate to it directly: this call site's
 * `formatName` is a per-render closure bound to the surface's `isJapanese` (from
 * `useFormatName`/`NameFormattingContext`), not `resolveParticipantLabel`'s own
 * `isJapanese` parameter. A team display name and an alias both win and are
 * truncated with an ellipsis; a personal name goes through the script-aware
 * `formatName` (whose own long-name handling, "F. Surname", must never be
 * applied to a team name or an alias).
 */
export function drawPlayerLabel(
  player: LabelledPlayer,
  formatName: FormatName,
  options?: { maxLength?: number },
): string {
  const team = (player.displayName ?? '').trim();
  if (team) return truncateLabel(team, options?.maxLength);
  const alias = (player.alias ?? '').trim();
  if (alias) return truncateLabel(alias, options?.maxLength);
  return formatName(player.firstName, player.lastName, null, options);
}
